import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bleed, Button, Card, Option, Select, Text } from '@main/complykit';
import { useGetAvailableFrameworksForOrganizationQuery } from '@main/graphql/queries/GetAvailableFrameworksForOrganization.generated';
import { isNonNullable, toError } from '@main/shared/utils';
import { actionHelper, createColumnHelper, errorToast, Table, useAlertDialog } from '@main/ui';
import { useCallback, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import { useBackofficeSelector } from '../../hooks/redux-toolkit-hooks';
import { getOrgFrameworks } from './slice';
import {
  useAddOrgTemplateFrameworkMutation,
  useGetAvailableTemplateFrameworksQuery,
  useRemoveOrgTemplateFrameworkMutation,
} from './TemplateFrameworks.admin.generated';

const frameworkSchema = z.object({
  frameworkId: z.string({ error: 'Select a framework.' }),
});

const tableId = 'frameworks';

type FrameworkSchema = z.infer<typeof frameworkSchema>;
export const FrameworksTab = ({ orgId }: { orgId: string }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  useGetAvailableFrameworksForOrganizationQuery({ orgId });
  const frameworks = useBackofficeSelector((state) => getOrgFrameworks(state, orgId));
  const columns = useFrameworkTableColumns({ orgId });

  return (
    <Stack spacing={8}>
      <Card>
        <Bleed>
          <Table
            id={tableId}
            data={frameworks}
            columns={columns}
            itemName={{
              singular: 'framework',
              plural: 'frameworks',
            }}
          />
        </Bleed>
      </Card>
      <Box alignSelf="end">
        <Button iconStart={PlusIcon} onPress={onOpen}>
          Add framework
        </Button>
      </Box>
      <AddFrameworkModal
        isOpen={isOpen}
        onClose={onClose}
        orgId={orgId}
        orgFrameworks={frameworks.map((framework) => framework.id)}
      />
    </Stack>
  );
};

type FrameworkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  orgFrameworks: string[];
};

export const AddFrameworkModal = (props: FrameworkModalProps) => {
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    control,
  } = useForm<FrameworkSchema>({
    resolver: zodResolver(frameworkSchema),
    defaultValues: {
      frameworkId: undefined,
    },
  });
  const { refetch: refetchFrameworks } = useGetAvailableFrameworksForOrganizationQuery({
    orgId: props.orgId,
  });
  const [addFramework] = useAddOrgTemplateFrameworkMutation();
  const { frameworks } = useGetAvailableTemplateFrameworksQuery(
    { orgFrameworks: props.orgFrameworks },
    {
      selectFromResult: ({ data }) => {
        return { frameworks: data?.template_frameworks ?? [] };
      },
    },
  );

  const onSubmit: SubmitHandler<FrameworkSchema> = async (data) => {
    try {
      await addFramework({
        input: {
          organization_id: props.orgId,
          template_framework_id: data.frameworkId,
        },
      }).unwrap();

      await refetchFrameworks().unwrap();
      props.onClose();
    } catch (error) {
      errorToast('Failed to add framework to the organization');
      datadogLogs.logger.error(
        'Failed to add framework to the organization',
        { frameworkId: data.frameworkId, orgId: props.orgId },
        toError(error),
      );
    }
  };

  const options = useMemo(
    () => frameworks.map((framework) => ({ value: framework.id, label: framework.name })),
    [frameworks],
  );

  return (
    <Modal
      size="xl"
      isCentered
      autoFocus={false}
      isOpen={props.isOpen}
      onClose={props.onClose}
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody pt={4} pb={0}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={6} pb={4}>
              <Text size="lg" weight="bold">
                Add template framework
              </Text>

              <Controller
                control={control}
                name="frameworkId"
                render={({ field, fieldState }) => (
                  <Select
                    isFilterable
                    filterLabel="Filter options"
                    filterPlaceholder="Filter"
                    filterEmptyMessage="No matches"
                    emptyMessage="All active template frameworks are already enabled"
                    ref={field.ref}
                    label="Framework"
                    name={field.name}
                    value={field.value ?? null}
                    onChange={(value) => {
                      if (value) field.onChange(value);
                    }}
                    onBlur={field.onBlur}
                    isDisabled={field.disabled}
                    error={fieldState.error?.message}
                    placeholder="Select framework"
                    items={options}
                  >
                    {(option) => <Option id={option.value}>{option.label}</Option>}
                  </Select>
                )}
              />

              <Flex gap={2} justify="end">
                <Button
                  variant="soft"
                  onPress={() => {
                    props.onClose();
                    reset();
                  }}
                >
                  Cancel
                </Button>
                <Button isPending={isSubmitting} type="submit">
                  Submit
                </Button>
              </Flex>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type Framework = ReturnType<typeof getOrgFrameworks>[number];
export function useFrameworkTableColumns({ orgId }: { orgId: string }) {
  const { openDialog } = useAlertDialog();

  const { refetch: refetchFrameworks } = useGetAvailableFrameworksForOrganizationQuery({
    orgId,
  });

  const [removeTemplateFramework] = useRemoveOrgTemplateFrameworkMutation();
  const onRemove = useCallback(
    async (framework: Framework) => {
      try {
        await removeTemplateFramework({
          template_framework_id: framework.id,
          organization_id: orgId,
        }).unwrap();
        await refetchFrameworks().unwrap();
      } catch (error) {
        errorToast('Failed to remove framework from the organization');
        datadogLogs.logger.error(
          'Failed to remove framework from the organization',
          { framework, orgId },
          toError(error),
        );
      }
    },
    [orgId, refetchFrameworks, removeTemplateFramework],
  );

  return useMemo(() => {
    const columnHelper = createColumnHelper<Framework>();

    return [
      columnHelper.columns.tag({
        id: 'status',
        header: 'Status',
        accessorFn: ({ type }) => {
          return {
            value: type,
            colorScheme: 'purple',
          };
        },
        size: 110,
        enableColumnFilter: true,
      }),
      columnHelper.columns.text({
        id: 'name',
        header: 'Name',
        accessorFn: ({ name }) => name,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        enableHiding: false,
      }),

      columnHelper.columns.actions({
        menuActions: ({ row }) =>
          [
            row.original.type === 'Template'
              ? actionHelper.menuActionItem({
                  icon: TrashIcon,
                  label: 'Delete',
                  onClick: () =>
                    openDialog({
                      dialogHeader: 'Delete framework',
                      dialogContent: 'Are you sure you want to delete this framework?',
                      confirmAction: {
                        children: 'Delete',
                        onClick: () => onRemove(row.original),
                      },
                    }),
                })
              : null,
          ].filter(isNonNullable),
      }),
    ];
  }, [onRemove, openDialog]);
}
